import { Fragment, useState, type ComponentType } from "react";
import {
  BarChart3,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  DollarSign,
  DoorOpen,
  ExternalLink,
  FileDown,
  FileText,
  Fingerprint,
  Flag,
  Globe,
  LayoutGrid,
  Link,
  List,
  Mail,
  PenLine,
  Percent,
  Plus,
  Printer,
  Scale,
  Shield,
  Sparkles,
  Table,
} from "lucide-react";

import { AriIcon } from "@/components/sidebar/icons";
import { TextShimmer } from "@/components/primitives/text-shimmer";
import {
  Badge,
  Button,
  Card,
  Menu,
  Checkbox,
  ScrollArea,
} from "@/components/ui";
import { Tabs, TabsList, TabsPanel, TabsTab } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import {
  DC_CLIENT,
  DC_COMPOSITE,
  DC_DEADLINE,
  DC_DEAL_TYPE,
  DC_DELTA_NOTES,
  DC_DIMENSIONS,
  DC_OFFERS,
  DC_OFFER_BRAND_STYLES,
  type DealDimension,
  type DealOffer,
} from "./dealComparisonData";
import { AriBrandAvatar } from "@/components/demo-ari/AriBrandIcon";
import type { AriBrandId } from "@/lib/ariBrands";
import { riskScoreLabel, scoreTier, type ScoreTier } from "./risk";

type LucideIconComponent = ComponentType<{
  size?: number;
  className?: string;
}>;

const DIMENSION_ICONS: Record<string, LucideIconComponent> = {
  dollar: DollarSign,
  percent: Percent,
  calendar: Calendar,
  shield: Shield,
  list: List,
  globe: Globe,
  gavel: Scale,
  door: DoorOpen,
  pen: PenLine,
  fingerprint: Fingerprint,
  exit: ExternalLink,
};

type ViewMode = "table" | "cards" | "scorecard";

interface DealComparisonMainViewProps {
  onBack?: () => void;
  onOpenUpload?: () => void;
}

export function DealComparisonMainView({ onBack, onOpenUpload }: DealComparisonMainViewProps) {
  const [view, setView] = useState<ViewMode>("table");
  const [highlightDiffs, setHighlightDiffs] = useState(true);
  const [hiddenOffers, setHiddenOffers] = useState<Set<string>>(new Set());
  const [activeDimension, setActiveDimension] = useState<string | null>(null);

  const visibleOffers = DC_OFFERS.filter((o) => !hiddenOffers.has(o.id));

  const toggleOffer = (id: string) => {
    setHiddenOffers((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <Tabs
        value={view}
        onValueChange={(v) => {
          if (v === "table" || v === "cards" || v === "scorecard") setView(v);
        }}
        className="flex h-full min-h-0 flex-col"
      >
        <div className="sticky top-0 z-30 shrink-0 border-b border-border bg-background/95 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/90">
          <header className="flex items-start gap-3.5 px-6 py-4">
          {onBack && (
            <Button type="button" variant="outline" size="icon-sm" onClick={onBack} aria-label="Back">
              <ChevronLeft size={16} aria-hidden="true" />
            </Button>
          )}
        <img
          src={DC_CLIENT.avatar}
          alt={DC_CLIENT.name}
          className="size-10 shrink-0 rounded-full border border-border object-cover"
        />
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2 text-sm">
            <span className="font-semibold text-foreground">Deal Comparison</span>
            <span className="size-1 rounded-full bg-muted-foreground" />
            <span className="text-sm text-muted-foreground">{DC_DEAL_TYPE}</span>
          </div>
          <h1 className="text-lg font-semibold">
            {DC_CLIENT.name}{" "}
            <span className="font-normal text-muted-foreground">· 3 sneaker collab offers</span>
          </h1>
          <p className="mt-1 text-[11.5px] leading-snug text-muted-foreground">{DC_CLIENT.context}</p>
        </div>
        <Card className="min-w-[130px] shrink-0 px-3.5 py-2">
          <p className="text-[9.5px] text-muted-foreground">Decision by</p>
          <p className="text-sm font-semibold">{DC_DEADLINE}</p>
          <TextShimmer
            as="p"
            duration={2}
            repeatDelay={3}
            className="mt-0.5 text-[10.5px] font-medium [--base-color:var(--color-warning)] [--base-gradient-color:#ffffff] dark:[--base-color:var(--color-warning-foreground)] dark:[--base-gradient-color:#ffffff]"
          >
            ● 8 days remaining
          </TextShimmer>
        </Card>
      </header>

      <div className="flex items-center gap-2.5 overflow-x-auto border-b border-border bg-card px-6 py-3">
        <span className="shrink-0 text-[10px] font-semibold text-muted-foreground">
          Offers
        </span>
        {DC_OFFERS.map((o) => {
          const hidden = hiddenOffers.has(o.id);
          const brandStyle = DC_OFFER_BRAND_STYLES[o.id];
          return (
            <Button
              key={o.id}
              type="button"
              variant="outline"
              className={cn(
                "h-auto shrink-0 gap-2 px-3 py-1.5",
                !hidden && brandStyle?.pillClass,
                hidden && "opacity-50",
              )}
              onClick={() => toggleOffer(o.id)}
            >
              <AriBrandAvatar brandId={o.id as AriBrandId} size="sm" />
              <span className="text-left">
                <span className="block text-xs font-semibold">{o.brand}</span>
                <span
                  className={cn(
                    "block text-[10px]",
                    !hidden && brandStyle?.pillSubtextClass,
                    hidden && "text-muted-foreground",
                  )}
                >
                  {o.cash.headline} · {o.term.headline}
                </span>
              </span>
            </Button>
          );
        })}
        <Button
          type="button"
          variant="outline"
          className="h-auto shrink-0 border-dashed px-3 py-1.5"
          onClick={onOpenUpload}
        >
          <Plus size={14} aria-hidden="true" />
          Add offer
        </Button>
      </div>

          <div className="flex flex-wrap items-center gap-2 px-6 py-2.5">
          <TabsList className="text-sm">
            <TabsTab value="table" className="gap-1.5 px-2.5 py-1 text-sm">
              <Table size={12} aria-hidden="true" />
              Table
            </TabsTab>
            <TabsTab value="cards" className="gap-1.5 px-2.5 py-1 text-sm">
              <LayoutGrid size={12} aria-hidden="true" />
              Cards
            </TabsTab>
            <TabsTab value="scorecard" className="gap-1.5 px-2.5 py-1 text-sm">
              <BarChart3 size={12} aria-hidden="true" />
              Scorecard
            </TabsTab>
          </TabsList>

        <div className="flex items-center gap-2">
          <Checkbox
            id="deal-comparison-highlight-diffs"
            checked={highlightDiffs}
            onCheckedChange={(checked) => setHighlightDiffs(checked === true)}
          />
          <label
            htmlFor="deal-comparison-highlight-diffs"
            className="flex cursor-pointer items-center gap-1.5 text-sm text-muted-foreground select-none"
          >
            Highlight differences
          </label>
        </div>

        <span className="ml-auto text-sm text-muted-foreground">
          {visibleOffers.length}/{DC_OFFERS.length} offers · {DC_DIMENSIONS.length} dimensions
        </span>

        <Menu>
          <Menu.Trigger>
            <Button type="button" size="sm">
              <FileDown size={14} aria-hidden="true" />
              Export
              <ChevronDown size={12} aria-hidden="true" />
            </Button>
          </Menu.Trigger>
          <Menu.Popup align="end" className="w-72">
            <p className="px-2.5 py-1.5 text-sm font-semibold text-muted-foreground">
              Export comparison
            </p>
            {[
              { icon: FileText, title: "Client one-pager (PDF)", desc: "Branded summary, decision-ready" },
              { icon: Table, title: "Side-by-side (CSV)", desc: "Spreadsheet for analysis" },
              { icon: Mail, title: "Email summary", desc: "Pasteable text · plain English" },
              { icon: Link, title: "Shareable link", desc: "Read-only · expires 7 days" },
              { icon: Printer, title: "Print", desc: "Direct to printer" },
            ].map((x) => (
              <Menu.Item key={x.title} className="gap-2.5 py-2">
                <span className="flex size-7 items-center justify-center rounded-md bg-muted text-primary">
                  <x.icon size={14} aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs font-medium">{x.title}</span>
                  <span className="block text-xs text-muted-foreground">{x.desc}</span>
                </span>
              </Menu.Item>
            ))}
          </Menu.Popup>
        </Menu>
          </div>
        </div>

        <ScrollArea className="min-h-0 flex-1">
          <TabsPanel value="table">
            <TableView
              offers={visibleOffers}
              highlight={highlightDiffs}
              activeDimension={activeDimension}
              setActiveDimension={setActiveDimension}
            />
          </TabsPanel>
          <TabsPanel value="cards">
            <CardView offers={visibleOffers} highlight={highlightDiffs} />
          </TabsPanel>
          <TabsPanel value="scorecard">
            <ScorecardView offers={visibleOffers} />
          </TabsPanel>
        </ScrollArea>
      </Tabs>
    </div>
  );
}

const DIMENSION_GROUP_ICON: Record<
  DealDimension["group"],
  { container: string; icon: string }
> = {
  $: { container: "bg-success/15", icon: "text-success-foreground" },
  time: { container: "bg-info/12", icon: "text-info-foreground" },
  rights: { container: "bg-warning/15", icon: "text-warning-foreground" },
  work: { container: "bg-primary/10", icon: "text-primary" },
};

function DimIcon({
  name,
  group,
  className,
}: {
  name: string;
  group?: DealDimension["group"];
  className?: string;
}) {
  const Icon = DIMENSION_ICONS[name] ?? FileText;
  const tone = group ? DIMENSION_GROUP_ICON[group] : null;
  return (
    <span
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-md",
        tone?.container ?? "bg-muted",
        className,
      )}
    >
      <Icon size={14} aria-hidden="true" className={tone?.icon ?? "text-muted-foreground"} />
    </span>
  );
}

function TableView({
  offers,
  highlight,
  activeDimension,
  setActiveDimension,
}: {
  offers: DealOffer[];
  highlight: boolean;
  activeDimension: string | null;
  setActiveDimension: (id: string | null) => void;
}) {
  return (
    <div className="px-6 py-4 pb-8">
      <table className="w-full border-separate border-spacing-x-3 border-spacing-y-2 text-xs">
        <thead>
          <tr>
            <th className="sticky left-0 top-0 z-10 w-52 bg-background pb-3 text-left text-[10.5px] font-semibold text-muted-foreground">
              Dimension
            </th>
            {offers.map((o) => (
              <th key={o.id} className="sticky top-0 z-[9] bg-background pb-3 text-left">
                <Card className={cn("flex-row items-center gap-2.5 p-2.5", o.accentClass)}>
                  <AriBrandAvatar brandId={o.id as AriBrandId} size="md" className="rounded-md" />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-semibold">{o.brand}</span>
                    <span className="block truncate text-[10px] text-muted-foreground">{o.division}</span>
                  </span>
                </Card>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DC_DIMENSIONS.map((dim, i) => {
            const note = DC_DELTA_NOTES[dim.id];
            const isActive = activeDimension === dim.id;
            return (
              <Fragment key={dim.id}>
                <tr
                  className="cursor-pointer"
                  onClick={() => setActiveDimension(isActive ? null : dim.id)}
                >
                  <td className="sticky left-0 z-[8] bg-background py-3 pr-3 align-top">
                    <div className="flex items-start gap-2">
                      <DimIcon name={dim.icon} group={dim.group} />
                      <span>
                        <span className="block text-xs font-medium">{dim.label}</span>
                        <WeightBadge weight={dim.weight} />
                      </span>
                    </div>
                  </td>
                  {offers.map((o) => (
                    <CellTd key={o.id} offer={o} dim={dim} highlight={highlight} note={note} />
                  ))}
                </tr>
                {isActive && note && (
                  <tr>
                    <td colSpan={offers.length + 1} className="bg-primary/5 px-3 pb-4 pt-1">
                      <p className="flex items-start gap-2 text-[11.5px] leading-relaxed text-muted-foreground">
                        <Sparkles size={12} aria-hidden="true" className="mt-0.5 shrink-0 text-primary" />
                        <span>
                          <strong className="text-primary">What differs:</strong> {note.note}
                        </span>
                      </p>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function WeightBadge({ weight }: { weight: DealDimension["weight"] }) {
  const label =
    weight === "high" ? "● High weight" : weight === "med" ? "● Medium" : "○ Low";
  return (
    <span className="mt-0.5 block text-[9.5px] text-muted-foreground">
      {label}
    </span>
  );
}

function CellTd({
  offer: o,
  dim,
  highlight,
  note,
}: {
  offer: DealOffer;
  dim: DealDimension;
  highlight: boolean;
  note?: { winner: string; note: string };
}) {
  const cell = o[dim.id];
  const isWinner = highlight && note?.winner === o.id;
  const hasFlag = highlight && cell.flag;

  return (
    <td className="min-w-[200px] align-top p-0">
      <div
        className={cn(
          "rounded-xl p-3 bg-card",
          isWinner && o.accentClass,
          hasFlag && "bg-[#ff4a2e]",
          isWinner && "bg-primary",
        )}
      >
      <div className="mb-1 flex flex-wrap items-baseline gap-1.5">
        <span
          className={cn(
            "text-sm font-semibold",
            (dim.id === "cash" || dim.id === "royalty") && "font-mono",
          )}
        >
          {cell.headline}
        </span>
        <span className={cn("text-xs text-muted-foreground", {
          "bg-white text-primary px-2 py-0.5 rounded-full": isWinner,
          "text-white": hasFlag,
        })}>{cell.sub}</span>
        {hasFlag && <div className="ml-auto grid size-4.5 place-items-center rounded-full bg-white"><Flag size={10} aria-hidden="true" className="text-[#ff4a2e]" /></div>}
        {isWinner && <div className="ml-auto grid size-4.5 place-items-center rounded-full bg-white"><Check size={12} aria-hidden="true" className="text-primary" /></div>}
      </div>
      <p className={cn("text-[11px] leading-snug text-muted-foreground mb-3 font-semibold", {
        "text-primary-foreground": isWinner,
        "text-white": hasFlag,
      })}>{cell.detail}</p>
      {(cell.bonuses || cell.equity || cell.total || cell.renewal) && (
        <p className={cn("mt-1.5 rounded-full border border-border w-fit px-2 py-1 text-[10px] text-muted-foreground", {
          "bg-primary text-primary-foreground border-black/10 dark:border-white/50": isWinner,
          "bg-[#ff4a2e] text-white border-white/70": hasFlag,
        })}>
          {cell.bonuses || cell.equity || cell.total || cell.renewal}
        </p>
      )}
      </div>
    </td>
  );
}

function CardDimensionRow({
  dim,
  cell,
  highlight,
  offerId,
}: {
  dim: DealDimension;
  cell: DealOffer[typeof dim.id];
  highlight: boolean;
  offerId: string;
}) {
  const note = DC_DELTA_NOTES[dim.id];
  const isWinner = highlight && note?.winner === offerId;
  const hasFlag = highlight && cell.flag;

  return (
    <div
      className={cn(
        "rounded-2xl px-3.5 py-3",
        "bg-muted/15",
        isWinner && !hasFlag && "bg-primary/[0.04]",
        hasFlag && "bg-destructive/[0.04]",
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">{dim.label}</span>
        {hasFlag ? (
          <span className="shrink-0 text-[10px] font-medium text-destructive-foreground">
            Needs review
          </span>
        ) : isWinner ? (
          <span className="shrink-0 text-[10px] font-medium text-primary">Best</span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-sm font-semibold tabular-nums text-foreground">{cell.headline}</span>
        {cell.sub ? (
          <span className="text-xs text-muted-foreground">{cell.sub}</span>
        ) : null}
      </div>
      {cell.detail ? (
        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{cell.detail}</p>
      ) : null}
    </div>
  );
}

function CardView({ offers, highlight }: { offers: DealOffer[]; highlight: boolean }) {
  return (
    <div
      className="grid gap-4 px-6 py-4 pb-8"
      style={{ gridTemplateColumns: `repeat(${Math.max(offers.length, 1)}, minmax(0, 1fr))` }}
    >
      {offers.map((o) => (
        <Card key={o.id} className="overflow-hidden rounded-3xl border-border/80 p-0 shadow-none bg-transparent">
          <div className={cn("px-4 py-4", o.swatchClass)}>
            <div className="flex gap-3">
              <AriBrandAvatar brandId={o.id as AriBrandId} size="lg" className="rounded-lg" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{o.brand}</p>
                <p className="text-xs text-white/70">{o.division}</p>
                <p className="mt-1 text-[11px] text-white/55">
                  Submitted {o.submitted} · {o.rep}
                </p>
              </div>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-white/80">{o.summary}</p>
          </div>
          <div className="flex flex-col gap-2.5 p-3">
            {DC_DIMENSIONS.map((dim) => (
              <CardDimensionRow
                key={dim.id}
                dim={dim}
                cell={o[dim.id]}
                highlight={highlight}
                offerId={o.id}
              />
            ))}
          </div>
          <div className="flex gap-2 border-t border-border p-3">
            <Button type="button" variant="outline" size="sm" className="flex-1">
              <FileText size={12} aria-hidden="true" />
              View full
            </Button>
            <Button type="button" size="sm" className="flex-1">
              <Check size={12} aria-hidden="true" />
              Accept
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

const SCORE_TIER_BADGE: Record<
  ScoreTier,
  { variant: "success" | "warning" | "error"; className?: string }
> = {
  strong: { variant: "success", className: "bg-success/15 text-success-foreground ring-1 ring-success/25" },
  watch: { variant: "warning", className: "bg-warning/15 text-warning-foreground ring-1 ring-warning/25" },
  concern: { variant: "error", className: "bg-destructive/12 text-destructive-foreground ring-1 ring-destructive/20" },
};

const DIMENSION_WEIGHT_BADGE: Record<
  DealDimension["weight"],
  { variant: "warning" | "info" | "secondary"; label: string; className?: string }
> = {
  high: {
    variant: "warning",
    label: "High weight",
    className: "bg-warning/15 text-warning-foreground ring-1 ring-warning/25",
  },
  med: {
    variant: "info",
    label: "Medium",
    className: "bg-info/12 text-info-foreground ring-1 ring-info/20",
  },
  low: {
    variant: "secondary",
    label: "Low",
    className: "bg-muted text-muted-foreground ring-1 ring-border",
  },
};

function ScoreTierBadge({ score }: { score: number }) {
  const tier = scoreTier(score);
  const config = SCORE_TIER_BADGE[tier];
  return (
    <Badge
      variant={config.variant}
      className={cn(
        "rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide",
        config.className,
      )}
    >
      {riskScoreLabel(score)}
    </Badge>
  );
}

function DimensionWeightBadge({ weight }: { weight: DealDimension["weight"] }) {
  const config = DIMENSION_WEIGHT_BADGE[weight];
  return (
    <Badge
      variant={config.variant}
      className={cn(
        "rounded-full px-2.5 py-0.5 text-[10px] font-semibold tracking-wide",
        config.className,
      )}
    >
      {config.label}
    </Badge>
  );
}

function ScorecardOfferCard({ offer }: { offer: DealOffer }) {
  const composite = DC_COMPOSITE[offer.id];
  if (!composite) return null;

  const metrics = [
    { id: "financial" as const, label: "Financial value" },
    { id: "flexibility" as const, label: "Flexibility" },
    { id: "rights" as const, label: "Rights & control" },
    { id: "workload" as const, label: "Workload" },
  ];

  return (
    <Card className="overflow-hidden rounded-3xl border-border/80 p-0 shadow-none">
      <div className="flex items-start gap-3 border-b border-border px-4 py-3.5">
        <AriBrandAvatar brandId={offer.id as AriBrandId} size="md" className="rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">{offer.brand}</p>
          <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
            {offer.cash.headline}
            <span className="mx-1 text-border">·</span>
            {offer.term.headline}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <p className="text-2xl font-semibold tabular-nums leading-none tracking-tight text-foreground">
            {composite.overall}
          </p>
          <ScoreTierBadge score={composite.overall} />
        </div>
      </div>

      <dl className="px-4 py-1">
        {metrics.map((metric) => (
          <div
            key={metric.id}
            className="flex items-center justify-between gap-4 border-b border-border/60 py-2.5 last:border-b-0"
          >
            <dt className="text-xs text-muted-foreground">{metric.label}</dt>
            <dd className="text-sm font-medium tabular-nums text-foreground">{composite[metric.id]}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function DimensionCompareBar({
  offer,
  dim,
  score,
  isBest,
}: {
  offer: DealOffer;
  dim: DealDimension;
  score: number;
  isBest: boolean;
}) {
  const headline = offer[dim.id].headline;

  return (
    <div className="flex items-center gap-2.5">
      <span className="flex w-6 shrink-0 justify-center">
        <AriBrandAvatar brandId={offer.id as AriBrandId} size="sm" />
      </span>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <span
            className={cn(
              "truncate text-xs font-medium tabular-nums text-foreground",
              (dim.id === "cash" || dim.id === "royalty") && "tracking-tight",
            )}
          >
            {headline}
          </span>
          <span
            className={cn(
              "shrink-0 text-xs tabular-nums",
              isBest ? "font-medium text-foreground" : "text-muted-foreground",
            )}
          >
            {score}
          </span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              "h-full rounded-full bg-foreground/15 transition-[width] duration-300 ease-out",
              isBest && "bg-foreground/30",
            )}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function ScorecardView({ offers }: { offers: DealOffer[] }) {
  return (
    <div className="space-y-6 px-6 py-5 pb-8">
      <div
        className="grid gap-3.5"
        style={{ gridTemplateColumns: `repeat(${Math.max(offers.length, 1)}, 1fr)` }}
      >
        {offers.map((o) => (
          <ScorecardOfferCard key={o.id} offer={o} />
        ))}
      </div>

      <Card className="overflow-hidden rounded-3xl p-4">
        <p className="mb-3.5 flex items-center gap-2 text-[11px] font-semibold">
          <BarChart3 size={12} aria-hidden="true" className="text-primary" />
          Dimension-by-dimension scoring
        </p>
        <div className="space-y-3">
          {DC_DIMENSIONS.map((dim) => {
            const note = DC_DELTA_NOTES[dim.id];
            return (
              <div
                key={dim.id}
                className="rounded-2xl border border-border/60 bg-muted/10 p-3.5"
              >
                <div className="mb-1.5 flex flex-wrap items-center gap-2">
                  <DimIcon name={dim.icon} group={dim.group} />
                  <span className="text-[11.5px] font-medium">{dim.label}</span>
                  <DimensionWeightBadge weight={dim.weight} />
                  {note && (
                    <span className="ml-auto text-[10.5px] text-muted-foreground">
                      Best:{" "}
                      <strong className="text-foreground">
                        {DC_OFFERS.find((x) => x.id === note.winner)?.brand}
                      </strong>
                    </span>
                  )}
                </div>
                <div
                  className="grid gap-2"
                  style={{ gridTemplateColumns: `repeat(${offers.length}, 1fr)` }}
                >
                  {offers.map((o) => {
                    const score = o[dim.id].score ?? 50;
                    const max = Math.max(...offers.map((x) => x[dim.id].score ?? 50));
                    return (
                      <DimensionCompareBar
                        key={o.id}
                        offer={o}
                        dim={dim}
                        score={score}
                        isBest={score === max}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <AlertInsight />
    </div>
  );
}

function AlertInsight() {
  return (
    <div className="flex gap-2.5 rounded-2xl bg-muted p-3.5 text-foreground">
      <AriIcon variant="gradient" className="mt-0.5 size-[1.05rem] shrink-0" aria-hidden="true" />
      <p className="text-sm leading-relaxed">
        <strong>Difference summary · </strong>
        Adidas leads on <strong>headline cash</strong> (+$27M over Nike, +$34M over NB) but has the
        most concerning rights structure: one-way morals, brand-led termination, perpetual IP, and
        18-mo non-compete. Nike is the steady mid-option with strongest royalty escalator. New Balance
        has the most flexible terms and cleanest rights, with a meaningful equity sweetener but lower
        guarantee.
      </p>
    </div>
  );
}
